import type { RecordBase } from './model';
import type { Role } from './campaigns';
import type { DistributionChannel, DistributionState } from './distribution';

/* ------------------------------------------------------------------------------------------
 * Publicação por API (etapa D). Somente o gateway simulado existe: nenhum modo publica de verdade.
 *
 * Contrato para qualquer adaptador REAL futuro (não existe nesta etapa): o provedor precisa
 * oferecer idempotência do lado dele pela `idempotencyKey` (um segundo envio com a mesma chave
 * devolve o post existente, sem criar outro) OU uma consulta autoritativa pela chave (`lookup` que
 * distingue "não existe" de "não sei"). Sem uma das duas garantias, nenhuma nova tentativa é
 * segura. Por isso, mesmo com elas, "não encontrado" comprovado (duas consultas após a carência)
 * leva a `failed{not_created}` SEM reenvio automático: só uma pessoa pede a nova tentativa. O
 * cancelamento por `AbortSignal` no timeout não prova que nada foi criado: depois de `sending`,
 * timeout continua sendo resultado desconhecido.
 * ---------------------------------------------------------------------------------------- */

/** `off`: nenhuma publicação por API; `fake`: gateway simulado em memória, nunca em produção. */
export type PublishMode = 'off' | 'fake';
export const publishSimulatedBanner = 'Modo simulado — nada é publicado';
/**
 * Faixa do modo desligado quando ainda há envios criados no modo simulado: só cancelar, revogar a
 * autorização e registrar a conferência manual continuam disponíveis (nenhuma chama o provedor).
 */
export const publishOffBanner =
  'Publicação por API desligada — envios pendentes só podem ser cancelados ou resolvidos';

export const publishRecordKinds = {
  connection: 'publish_connection',
  authorization: 'publish_authorization',
  job: 'publish_job',
} as const;

/* ---------------------------------- Estados do job ---------------------------------------- */

export const publishJobStates = [
  'queued',
  'leased',
  'sending',
  'confirmed',
  'failed',
  'unknown_outcome',
  'cancelled',
] as const;
export type PublishJobState = (typeof publishJobStates)[number];
/** Estados finais para o executor. `cancelled` só volta à fila por uma nova autorização. */
export const publishTerminalStates: readonly PublishJobState[] = ['confirmed', 'cancelled'];
/**
 * Transições permitidas. `sending` é gravado e confirmado ANTES da chamada ao provedor; nunca há
 * envio a partir de `unknown_outcome` (somente consulta pela chave de idempotência).
 */
export const publishTransitions: Readonly<Record<PublishJobState, readonly PublishJobState[]>> = {
  // → unknown_outcome: uma resposta descartada (lease perdida) disse que a publicação existe.
  queued: ['leased', 'cancelled', 'unknown_outcome'],
  // leased → leased: lease vencida reassumida (nada foi enviado); → queued: pré-envio sem prazo
  // (lease devolvida sem gastar tentativa) ou falha retentável; → failed: pré-envio definitivo.
  leased: ['leased', 'sending', 'queued', 'failed', 'cancelled', 'unknown_outcome'],
  // → queued: falha retentável (`PublishError{created:false}`) com tentativas restantes.
  sending: ['confirmed', 'failed', 'queued', 'unknown_outcome', 'cancelled'],
  // "Não criado" comprovado vira `failed` sem reenvio automático (só nova tentativa manual).
  unknown_outcome: ['confirmed', 'failed', 'cancelled'],
  failed: ['queued', 'cancelled', 'unknown_outcome'],
  confirmed: [],
  cancelled: ['queued', 'unknown_outcome'],
};
/**
 * Um job não terminal é dono do destino: o registro manual (`planSaveDistribution`,
 * `planConfirmDistribution`) é recusado até o job ser cancelado ou concluído.
 */
export const publishJobBlocksManual = (job: { state: PublishJobState }) =>
  !publishTerminalStates.includes(job.state);
/** Rótulos curtos para a interface. */
export const publishJobStateLabels: Record<PublishJobState, string> = {
  queued: 'Na fila',
  leased: 'Em preparação',
  sending: 'Em envio',
  confirmed: 'Confirmada',
  failed: 'Falhou',
  unknown_outcome: 'Conferindo no provedor',
  cancelled: 'Cancelada',
};
/** Espelho em `destination_distribution` (mesmo plano de gravação do job). */
export const publishDistributionState: Readonly<Record<PublishJobState, DistributionState>> = {
  queued: 'pronta',
  leased: 'em_envio',
  sending: 'em_envio',
  confirmed: 'confirmada',
  failed: 'falhou',
  unknown_outcome: 'desconhecido',
  cancelled: 'cancelada',
};

/* ---------------------------------- Falhas e bloqueios ------------------------------------ */

export const publishFailureCodes = [
  'media_not_accessible',
  'media_unreachable',
  'media_hash_mismatch',
  'media_format_rejected',
  'media_too_large',
  'carousel_count',
  'rate_limit',
  'partial_carousel',
  'not_created',
  'interrupted',
  'auth_revoked',
  'provider_rejected',
  'external_id_conflict',
] as const;
export type PublishFailureCode = (typeof publishFailureCodes)[number];
/** Retentáveis voltam à fila com espera; definitivas de mídia exigem nova revisão do pacote. */
export const publishFailureRetryable: Readonly<Record<PublishFailureCode, boolean>> = {
  media_not_accessible: true,
  media_unreachable: true,
  media_hash_mismatch: false,
  media_format_rejected: false,
  media_too_large: false,
  carousel_count: false,
  rate_limit: true,
  partial_carousel: true,
  not_created: true,
  interrupted: true,
  auth_revoked: false,
  provider_rejected: false,
  external_id_conflict: false,
};
/** Falhas que só uma nova revisão do pacote resolve (uma nova autorização não reabre o job). */
export const publishRevisionFailures: readonly PublishFailureCode[] = [
  'media_hash_mismatch',
  'media_format_rejected',
  'media_too_large',
  'carousel_count',
];
export const publishFailureMessages: Readonly<Record<PublishFailureCode, string>> = {
  media_not_accessible:
    'A mídia exige credencial ou recusou o acesso público. Nova tentativa disponível.',
  media_unreachable: 'A mídia não respondeu no endereço informado. Nova tentativa disponível.',
  media_hash_mismatch:
    'O arquivo publicado no link difere do SHA-256 aprovado. Prepare uma nova revisão.',
  media_format_rejected:
    'O formato da mídia não é aceito pelo adaptador simulado. Prepare uma nova revisão.',
  media_too_large: 'A mídia excede o limite do adaptador simulado. Prepare uma nova revisão.',
  carousel_count: 'A quantidade de itens não cabe no formato aprovado. Prepare uma nova revisão.',
  rate_limit: 'Limite de envios do provedor simulado atingido. Nova tentativa após a espera.',
  partial_carousel:
    'O carrossel ficou incompleto e não foi publicado. A nova tentativa reaproveita os itens já criados.',
  not_created: 'O provedor confirmou que a publicação não foi criada. Nova tentativa disponível.',
  interrupted: 'A preparação foi interrompida antes do envio. Nova tentativa disponível.',
  auth_revoked:
    'O provedor recusou a autorização da conexão. Reconecte a conta e autorize novamente.',
  provider_rejected:
    'O provedor recusou a publicação sem criá-la. Confira o pacote antes de autorizar novamente.',
  external_id_conflict:
    'O identificador devolvido pelo provedor já pertence a outro registro. Confira manualmente.',
};

export const publishBlockCodes = [
  'package_not_current',
  'package_changed',
  'context_changed',
  'destination_removed',
  'material_changed',
  'media_not_public',
  'channel_unsupported',
  'authorization_inactive',
  'authorizer_invalid',
  'connection_invalid',
  'unresolved_predecessor',
  'concurrent_job',
  'destination_pending',
  'already_published',
  'outside_approved_date',
  'schedule_mismatch',
  'not_due',
] as const;
export type PublishBlockCode = (typeof publishBlockCodes)[number];
export type PublishValidity = { ok: true } | { ok: false; code: PublishBlockCode; reason: string };

/* ---------------------------------- Registros --------------------------------------------- */

/** `pconn:${destinationId}`; destinationId = sha256(canal:conta) como em `normalizePackageInput`. */
export interface PublishConnection extends RecordBase {
  destinationId: string;
  channel: DistributionChannel;
  account: string;
  provider: 'fake';
  status: 'active' | 'revoked';
  /** Incrementa a cada revogação e reativação: autorizações antigas deixam de valer. */
  generation: number;
  ownerMemberId: string | null;
  createdBy: string;
  revokedAt: string | null;
  revokedBy: string;
  revokedReason: string;
}
export interface PublishAuthorizationDestination {
  destinationId: string;
  channel: DistributionChannel;
  account: string;
  connectionId: string;
  connectionGeneration: number;
}
export interface PublishMediaHash {
  materialId: string;
  sha256: string;
  url: string;
}
export interface PublishAuthorizer {
  /** null: principal local sem login (equivale a administrador, como no restante do painel). */
  memberId: string | null;
  role: Role;
  label: string;
}
export interface PublishAuthorization extends RecordBase {
  campaignId: string;
  contentId: string;
  packageId: string;
  packageRevision: number;
  packageHash: string;
  destinations: PublishAuthorizationDestination[];
  mediaHashes: PublishMediaHash[];
  mode: 'now' | 'scheduled';
  dueAt: string;
  status: 'active' | 'revoked' | 'completed';
  authorizedBy: PublishAuthorizer;
  authorizedAt: string;
  revokedBy: string;
  revokedAt: string | null;
  revokedReason: string;
}
export interface PublishAttempt {
  /** `${jobId}:${n}`, n = número da tentativa. */
  attemptId: string;
  n: number;
  startedAt: string;
  /** Instante gravado (e commitado) imediatamente antes da chamada ao provedor. */
  sentAt: string | null;
  finishedAt: string | null;
  result: 'pending' | 'confirmed' | 'failed' | 'unknown' | 'cancelled' | 'interrupted';
  errorCode: PublishFailureCode | null;
}
export interface PublishLookup {
  attemptId: string;
  at: string;
  result: 'found' | 'not_found' | 'inconclusive';
}
/**
 * Uma resposta descartada (lease perdida: consulta ou envio) afirmou que a publicação existe. O job
 * fica para conferência manual: "não encontrado" depois disso nunca comprova "não criado" e não há
 * nova tentativa até a conferência.
 */
export interface PublishDiscardedFound {
  at: string;
  source: 'lookup' | 'publish';
  externalId: string;
  url: string;
}
export interface PublishPreflightItem {
  materialId: string;
  url: string;
  reachable: boolean;
  accessible: boolean;
  sha256: string | null;
  size: number | null;
  mime: string | null;
}
export interface PublishPreflight {
  checkedAt: string;
  ok: boolean;
  /** Rótulo das regras usadas: "capacidade do adaptador simulado v1". */
  rules: string;
  items: PublishPreflightItem[];
  failure: { code: PublishFailureCode; message: string } | null;
}
export interface PublishFailure {
  code: PublishFailureCode;
  retryable: boolean;
  message: string;
  at: string;
}
export interface PublishChild {
  index: number;
  externalId: string;
}
/** Um job por (versão do pacote, destino); todas as tentativas usam a mesma chave. */
export interface PublishJob extends RecordBase {
  campaignId: string;
  contentId: string;
  packageId: string;
  packageRevision: number;
  packageHash: string;
  authorizationId: string;
  destinationId: string;
  channel: DistributionChannel;
  account: string;
  connectionId: string;
  connectionGeneration: number;
  format: string;
  /** sha256("flg-publish/v1:" + packageId + ":" + packageHash + ":" + destinationId). */
  idempotencyKey: string;
  state: PublishJobState;
  mode: 'now' | 'scheduled';
  dueAt: string;
  nextAttemptAt: string | null;
  attempts: number;
  /** Teto total; uma nova tentativa manual o estende sem zerar `attempts`. */
  attemptLimit: number;
  attemptLog: PublishAttempt[];
  lookups: PublishLookup[];
  nextLookupAt: string | null;
  /** Consultas esgotadas sem conclusão: só a conferência manual resolve. */
  manualCheckRequired: boolean;
  /** Ausente/null: nenhuma resposta descartada afirmou que a publicação existe. */
  discardedFound?: PublishDiscardedFound | null;
  preflight: PublishPreflight | null;
  carouselChildren: PublishChild[];
  externalId: string;
  url: string;
  publishedAt: string;
  publicationId: string | null;
  failure: PublishFailure | null;
  cancelRequestedAt: string | null;
  cancelReason: string;
  /** Fato confirmado depois que a autorização deixou de valer: anotado, nunca descartado. */
  confirmedAfterInvalidation: string | null;
  retryRequests: { requestedAt: string; requestedBy: string; previousAttempts: number }[];
  resolution: {
    outcome: 'published' | 'not_published';
    evidence: string;
    resolvedBy: string;
    resolvedAt: string;
  } | null;
  /** Lease persistida (interna; a projeção pública oculta executor, token e geração). */
  leaseExecutor?: string;
  leaseToken?: string;
  leaseGeneration?: number;
  leaseUntil: string | null;
  leasePurpose: 'send' | 'lookup' | null;
}
export type PublicPublishJob = Omit<
  PublishJob,
  'leaseExecutor' | 'leaseToken' | 'leaseGeneration'
> & {
  blockedReason: string | null;
  available: { cancel: boolean; retry: boolean; reconcile: boolean; resolve: boolean };
};

/* ---------------------------------- Visão pública ----------------------------------------- */

export interface PublishSummary {
  total: number;
  confirmed: number;
  failedRetryable: number;
  failedFinal: number;
  unknown: number;
  inProgress: number;
  queued: number;
  cancelled: number;
  notRequested: number;
  /** Ex.: "1 de 3 confirmados · 1 falhou (nova tentativa disponível) · 1 conferindo no provedor". */
  text: string;
}
export interface PackagePublishingDestination {
  destinationId: string;
  channel: DistributionChannel;
  account: string;
  connection: Pick<PublishConnection, 'id' | 'status' | 'generation' | 'version'> | null;
  distributionState: DistributionState;
  distributionMode: 'manual_record' | 'api';
  job: PublicPublishJob | null;
  /** Por que o destino não pode ser autorizado agora (ou por que o job ativo está bloqueado). */
  blockedReason: string | null;
  canAuthorize: boolean;
}
export interface PackagePublishing {
  packageId: string;
  packageVersion: number;
  campaignId: string;
  contentId: string;
  revision: number;
  status: string;
  hash: string;
  date: string;
  plannedTime: string;
  summary: PublishSummary;
  modes: { now: boolean; scheduled: boolean };
  blockedReason: string | null;
  authorizations: PublishAuthorization[];
  destinations: PackagePublishingDestination[];
}
export interface CampaignPublishing {
  mode: PublishMode;
  simulated: boolean;
  banner: string;
  rules: typeof fakeAdapterMediaRules | null;
  packages: PackagePublishing[];
  connections: PublishConnection[];
}

/* ---------------------------------- Rotas ------------------------------------------------- */

/**
 * Rotas idênticas nos dois runtimes; a autorização decisiva fica no planejador. `off: true`: a rota
 * continua disponível com a publicação por API desligada, porque nunca chama o provedor (cancelar,
 * revogar a autorização, conferência manual) — envios criados no modo simulado não prendem o
 * destino. As demais mutações respondem 501 no modo desligado.
 */
export const publishingRoutes = [
  {
    method: 'get',
    path: '/api/campaigns/:id/publishing',
    roles: ['admin', 'editor', 'approver', 'reader'],
    off: true,
  },
  {
    method: 'post',
    path: '/api/packages/:id/publish-authorizations',
    roles: ['admin', 'approver'],
    off: false,
  },
  {
    method: 'post',
    path: '/api/publishing/authorizations/:id/revoke',
    roles: ['admin', 'approver'],
    off: true,
  },
  {
    method: 'post',
    path: '/api/publishing/jobs/:id/cancel',
    roles: ['admin', 'approver', 'editor'],
    off: true,
  },
  {
    method: 'post',
    path: '/api/publishing/jobs/:id/retry',
    roles: ['admin', 'approver'],
    off: false,
  },
  {
    method: 'post',
    path: '/api/publishing/jobs/:id/reconcile',
    roles: ['admin', 'approver', 'editor'],
    off: false,
  },
  { method: 'post', path: '/api/publishing/jobs/:id/resolve', roles: ['admin'], off: true },
  { method: 'post', path: '/api/publishing/connections', roles: ['admin'], off: false },
  {
    method: 'post',
    path: '/api/publishing/connections/:id/revoke',
    roles: ['admin'],
    off: false,
  },
] as const satisfies readonly {
  method: 'get' | 'post';
  path: string;
  roles: readonly Role[];
  off: boolean;
}[];
/** Mutações que continuam disponíveis com a publicação por API desligada (nunca chamam o provedor). */
export const publishingOffRoutes: readonly string[] = publishingRoutes
  .filter((route) => route.method === 'post' && route.off)
  .map((route) => route.path);

/* ---------------------------------- Regras do adaptador simulado -------------------------- */

export interface FakeAdapterFormatRule {
  mimes: readonly string[];
  min: number;
  max: number;
  maxBytes: number;
}
const MB = 1024 * 1024;
const video = ['video/mp4', 'video/quicktime'] as const;
/**
 * Capacidade do adaptador SIMULADO v1. Não é documentação da Meta nem do YouTube: são os limites
 * deste gateway falso, usados para exercitar o pré-envio. Canal "Outro" não tem adaptador.
 */
export const fakeAdapterMediaRules = {
  version: 1,
  label: 'capacidade do adaptador simulado v1',
  disclaimer:
    'Limites do gateway simulado desta etapa; não reproduzem documentação da Meta nem do YouTube.',
  channels: {
    Instagram: {
      Reel: { mimes: video, min: 1, max: 1, maxBytes: 300 * MB },
      Carrossel: { mimes: ['image/jpeg'], min: 2, max: 10, maxBytes: 8 * MB },
      Post: { mimes: ['image/jpeg'], min: 1, max: 1, maxBytes: 8 * MB },
      Stories: { mimes: ['image/jpeg', 'video/mp4'], min: 1, max: 1, maxBytes: 100 * MB },
    },
    Facebook: {
      Reel: { mimes: video, min: 1, max: 1, maxBytes: 300 * MB },
      Carrossel: { mimes: ['image/jpeg', 'image/png'], min: 2, max: 10, maxBytes: 8 * MB },
      Post: { mimes: ['image/jpeg', 'image/png'], min: 1, max: 1, maxBytes: 8 * MB },
      Stories: { mimes: ['image/jpeg', 'video/mp4'], min: 1, max: 1, maxBytes: 100 * MB },
    },
    YouTube: {
      YouTube: { mimes: video, min: 1, max: 1, maxBytes: 2048 * MB },
      Reel: { mimes: video, min: 1, max: 1, maxBytes: 300 * MB },
    },
    Outro: {},
  },
} as const satisfies {
  version: number;
  label: string;
  disclaimer: string;
  channels: Record<DistributionChannel, Record<string, FakeAdapterFormatRule>>;
};
/** Regra do adaptador simulado para canal e formato; undefined = sem adaptador (409). */
export function fakeAdapterRule(
  channel: string,
  format: string,
): FakeAdapterFormatRule | undefined {
  const rules = (
    fakeAdapterMediaRules.channels as Record<string, Record<string, FakeAdapterFormatRule>>
  )[channel];
  return rules && Object.prototype.hasOwnProperty.call(rules, format) ? rules[format] : undefined;
}
